import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { useCallback, useContext, useMemo } from 'react';

import Checkbox from '~components/Checkbox/Checkbox';
import PrevailingWageBadge from '~components/Common/PrevailingWageBadge';
import { BottomColumn } from '~components/Order/ordersDispatchColumns';
import OrderStatusChip from '~components/Order/OrderStatusChip';
import OrderCard from '~components/OrderCard/OrderCard';
import OrderCardSubtitle from '~components/OrderCard/OrderCardSubtitle';
import OrderCardTitle from '~components/OrderCard/OrderCardTitle';
import { Order } from '~hooks/useOrders/models';

import DriverPayPageContext from '../../DriverPayPageContext';
import ExpandedBlock from './ExpandedBlock/ExpandedBlock';
import { StatsSlot } from './StatsSlot';

interface OrdersListCardProps {
  order: Order;
}

export default function OrdersListCard({ order }: OrdersListCardProps) {
  const {
    orderCardsExpandedState,
    toggleSingleOrderCardsExpandedState,
    getOrderCheckedState,
    toggleOrderCheck,
    currentCategory,
  } = useContext(DriverPayPageContext);

  const { orderId } = order;

  const isExpanded = useMemo(() => {
    const expandedState =
      orderCardsExpandedState.byOrderId[orderId] ?? orderCardsExpandedState.allCards;

    return expandedState === 'expanded';
  }, [orderCardsExpandedState]);

  const toggleExpanded = useCallback(() => {
    toggleSingleOrderCardsExpandedState(orderId, isExpanded ? 'collapsed' : 'expanded');
  }, [orderId, isExpanded]);

  const checkedState = getOrderCheckedState(order);

  return (
    <OrderCard>
      <Box
        onClick={toggleExpanded}
        sx={{
          alignItems: 'flex-start',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '20px 1fr auto',
          p: 1,
        }}
      >
        <Checkbox
          isChecked={checkedState === true}
          isIntermediate={checkedState === 'intermediate'}
          onChange={() => toggleOrderCheck(order)}
        />

        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}
        >
          <OrderCardTitle order={order} />
          <OrderCardSubtitle order={order} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {order.prevailingWage && <PrevailingWageBadge />}
          <OrderStatusChip order={order} hideDropdownArrow />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: 0.5,
            gridColumn: '2 / 4',
            overflow: 'hidden',
          }}
        >
          <BottomColumn
            statsSlot={<StatsSlot order={order} invoiceCategory={currentCategory} />}
            order={order}
            omitWrapper
          />
        </Box>
      </Box>

      <Collapse in={isExpanded} mountOnEnter unmountOnExit>
        <Box
          sx={(theme) => ({ borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}` })}
        >
          <ExpandedBlock order={order} />
        </Box>
      </Collapse>
    </OrderCard>
  );
}
